<template>
  <span
    class="images"
    :class="paddingClass"
    :style="{ paddingBottom }"
  >
    <!-- eslint-disable-next-line vue/no-restricted-html-elements -->
    <img
      v-if="image && (image.src || image.url)"
      class="images__image"
      :class="{
        [$class('loading')]: imageLoading && loadedAnimation,
        'was-loading': loadedAnimation,
      }"
      :alt="altAttribute"
      decoding="async"
      :fetchpriority="lazyload ? null : 'high'"
      :loading="lazyload ? 'lazy' : null"
      :role="altAttribute ? null : 'presentation'"
      :sizes="sizes"
      :src="srcAttribute"
      :srcset="srcsetAttribute.srcset"
      :style="styleAttribute"
      @load="setImageLoaded"
    >
  </span>
</template>

<script>

/**
 * Vue: Responsive image (images)
 * -----------------------------------------------------------------------------
 * Base template for responsive images.
 * - image.src comes from formatted product response.
 * - image.url comes from unformatted cart.js response.
 *
 * @param {Boolean|String} [alt] - Override image alt.
 * @param {Object} image - Image object formatted from Shopify response.
 * @param {Boolean} [lazyload] - Lazyload image, defaults to true.
 * @param {Boolean} [loadedAnimation] - Applies image loaded animation.
 * @param {Boolean} [padding] - Add padding based on image ratio or supplied
 * ratio, default to true, disable to make image fill its parent container.
 * @param {Boolean|Number|String} ratio - Aspect ratio of image in
 * `width:height`, `width / height`, or decimal ratio format (width/height),
 * will use the original ratio of image if not set, set to false or 'image'.
 * @param {String} sizes - Value for sizes attribute.
 * @param {String} [widths] - Comma and space separated list of srcset width
 * values, values greater than image's width are ignored, overrides default
 * srcset width value generation.
 *
 * @emits imageLoaded - Emit image loaded.
 *
 */
import {
  computeAspectRatio,
  computePaddingBottom,
  validateImage,
  validateRatio,
} from '~/helpers/vue'

export default {
  name: 'ResponsiveImage',

  props: {
    alt: {
      type: [Boolean, String],
      default: '',
    },
    image: {
      type: Object,
      default: () => ({}),
      required: true,
      validator: validateImage,
    },
    lazyload: {
      type: Boolean,
      default: true,
    },
    loadedAnimation: {
      type: Boolean,
      default: true,
    },
    padding: {
      type: Boolean,
      default: true,
    },
    ratio: {
      type: [Boolean, Number, String],
      default: false,
      validator: validateRatio,
      required: true,
    },
    sizes: {
      type: String,
      default: null,
    },
    widths: {
      type: String,
      default: '',
      validator: (value) => !value || value.match(/^\d+(?:,\s\d+)*$/g),
    },
  },

  emits: {
    'image-loaded': () => true,
  },

  data() {
    return {
      cnvsGlobal: true,
      imageLoading: true,
      imageMin: 360,
    }
  },

  computed: {

    /**
     * Compute alt attribute.
     * @returns {String}
     */
    altAttribute() {
      if (this.alt === false) {
        return ''
      }

      return typeof this.alt === 'boolean'
        ? this.image.alt ?? ''
        : this.alt ?? ''
    },

    /**
     * Compute aspect ratio.
     * @returns {String|Null}
     */
    aspectRatio() {
      if (!this.padding) {
        return null
      }

      return computeAspectRatio(this.ratio, this.image)
    },

    /**
     * Compute image max size.
     * @returns {Number}
     */
    imageMax() {
      return this.image?.width ? this.image.width : 2048
    },

    /**
     * Compute padding bottom.
     * @returns {String|Null}
     */
    paddingBottom() {
      if (!this.padding) {
        return null
      }

      return computePaddingBottom(this.aspectRatio)
    },

    /**
     * Compute image padding class.
     * @returns {String}
     */
    paddingClass() {
      return this.padding ? '' : 'images--no-padding'
    },

    /**
     * Compute src attribute.
     * - src attribute is only used in browsers which don't support srcset
     *   when w descriptors are used in srcset.
     * @returns {String}
     */
    srcAttribute() {
      return this.$formatImageUrl(
        this.image.src || this.image.url,
        { width: this.srcsetAttribute.max },
      )
    },

    /**
     * Compute srcset attribute.
     * - Uses either widths prop or image size to generate srcset sizes.
     * @returns {Object}
     */
    srcsetAttribute() {
      if (!this.image.src && !this.image.url) {
        return {
          srcset: null,
        }
      }

      const srcsetSizes = this.widths
        ? this.getSrcsetSizesFromWidths()
        : this.getSrcsetSizesFromImage()

      return {
        max: srcsetSizes[srcsetSizes.length - 1],
        min: srcsetSizes[0],
        srcset: this.getSrcset(srcsetSizes),
        srcsetSizes,
      }
    },

    /**
     * Compute style attribute.
     * - Used to apply image focal point styles.
     * @returns {String|Null}
     */
    styleAttribute() {
      return this.image.presentation?.focalPoint
        ? { objectPosition: this.image.presentation.focalPoint }
        : null
    },
  },

  methods: {

    /**
     * Set image as loaded.
     */
    async setImageLoaded() {
      await this.$nextTick()
      this.imageLoading = false
      this.$emit('image-loaded')
    },

    /**
     * Get srcset sizes from widths prop.
     * - Uses comma and space separated list of widths prop to generate srcset
     *   sizes, ignores values larger than image's width.
     * - If no image width is provided then fallback value of 2048px is used.
     * @returns {Array}
     */
    getSrcsetSizesFromWidths() {
      const widthsArray = this.widths.split(', ')
      return widthsArray.filter((size) => size <= this.imageMax)
    },

    /**
     * Get srcset sizes from image.
     * - Generates srcset sizes between 360px and image's maximum width in
     *   increments of 1.16x.
     * - If no image width is provided then fallback value of 2048px is used.
     * @returns {Array}
     */
    getSrcsetSizesFromImage() {
      const srcsetSizes = []
      let currentSize = this.imageMin

      while (currentSize < this.imageMax) {
        srcsetSizes.push(currentSize)
        currentSize = Math.round(currentSize * 1.16)
      }

      srcsetSizes.push(this.imageMax)
      return srcsetSizes
    },

    /**
     * Build srcset array.
     * @param {Array} srcsetSizes - Array of sizes to output.
     * @returns {Array}
     */
    getSrcset(srcsetSizes) {
      return srcsetSizes.map((size) => {
        const sizedImageUrl = this.$formatImageUrl(
          this.image.src || this.image.url,
          { width: size },
        )

        return `${sizedImageUrl} ${size}w`
      })
    },
  },
}
</script>
